import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtEUR, fmtDate, tradeLabel } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import UploadDevisDialog from "@/components/UploadDevisDialog";
import BulkUploadDialog from "@/components/BulkUploadDialog";
import AddDevisManualDialog from "@/components/AddDevisManualDialog";
import NegotiationDialog from "@/components/NegotiationDialog";
import MessagesDialog from "@/components/MessagesDialog";
import PublicLinkDialog from "@/components/PublicLinkDialog";
import DevisDetailsDialog from "@/components/DevisDetailsDialog";
import BudgetTable from "@/components/BudgetTable";
import OmissionsSection from "@/components/OmissionsSection";
import ChatAssistant from "@/components/ChatAssistant";
import EditDevisHeaderDialog from "@/components/EditDevisHeaderDialog";
import RevisionsDialog from "@/components/RevisionsDialog";
import ReliabilityBadge from "@/components/ReliabilityBadge";
import { toast } from "sonner";
import {
    ArrowLeft,
    AlertTriangle,
    FileText,
    Trash2,
    Sparkles,
    Loader2,
    Download,
    GitCompare,
    Building2,
    CheckCircle2,
    RotateCcw,
    StickyNote,
    ScanText,
    Award,
} from "lucide-react";

const STATUS_PILL = {
    en_attente: { c: "border-warn text-warn bg-warn/10", l: "En attente" },
    recu: { c: "border-ink/30 text-ink-soft bg-[#FAFAFA]", l: "Reçu" },
    en_negociation: {
        c: "border-accent text-accent bg-accent/10",
        l: "En négociation",
    },
    valide: { c: "border-ok text-ok bg-ok/10", l: "Validé" },
    rejete: { c: "border-danger text-danger bg-danger/10", l: "Rejeté" },
};

export default function ProjectDetailPage() {
    const { id } = useParams();
    const [project, setProject] = useState(null);
    const [devis, setDevis] = useState([]);
    const [duplicates, setDuplicates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [detecting, setDetecting] = useState(false);
    const [ocringId, setOcringId] = useState(null);
    const [showOnlySigned, setShowOnlySigned] = useState(false);

    const load = async () => {
        try {
            const [p, d, dup] = await Promise.all([
                api.getProject(id),
                api.listDevis(id),
                api.listDuplicates(id),
            ]);
            setProject(p);
            setDevis(d);
            setDuplicates(dup);
        } catch (e) {
            toast.error("Erreur de chargement");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [id]);

    const detect = async () => {
        if (devis.length < 2) {
            toast.error("Il faut au moins 2 devis pour détecter des doublons");
            return;
        }
        setDetecting(true);
        try {
            const res = await api.detectDuplicates(id, "claude");
            setDuplicates(res.alerts);
            toast.success(
                `${res.count} doublon${res.count > 1 ? "s" : ""} détecté${
                    res.count > 1 ? "s" : ""
                }`
            );
        } catch (e) {
            console.error("detectDuplicates failed:", e);
            toast.error("Erreur de détection");
        } finally {
            setDetecting(false);
        }
    };

    const deleteDevis = async (devisId) => {
        if (!window.confirm("Supprimer ce devis ?")) return;
        await api.deleteDevis(devisId);
        load();
    };

    const forceOCR = async (d) => {
        if (
            !window.confirm(
                `Re-extraire "${d.contractor_name}" via OCR Gemini Vision ? Les postes actuels seront remplacés. (Cela prend 15-40s.)`
            )
        )
            return;
        setOcringId(d.id);
        const t = toast.loading("OCR Gemini Vision en cours…");
        try {
            const res = await api.forceOCR(d.id);
            toast.success(
                `OCR terminé : ${res.line_items?.length || 0} postes extraits`,
                { id: t }
            );
            load();
        } catch (e) {
            console.error("force OCR failed:", e);
            toast.error(
                e?.response?.data?.detail || "Erreur OCR — réessayez plus tard",
                { id: t }
            );
        } finally {
            setOcringId(null);
        }
    };

    const toggleValidate = async (d) => {
        const newStatus = d.status === "valide" ? "recu" : "valide";
        try {
            await api.updateDevis(d.id, { status: newStatus });
            toast.success(
                newStatus === "valide"
                    ? `Devis "${d.contractor_name}" validé`
                    : "Validation retirée"
            );
            load();
        } catch (e) {
            console.error("toggleValidate failed:", e);
            toast.error("Erreur");
        }
    };

    const toggleSign = async (d) => {
        try {
            if (d.is_signed) {
                if (!window.confirm(
                    `Annuler la signature de "${d.contractor_name}" ? Le devis sortira de l'étude de marché.`
                )) return;
                await api.unsignDevis(d.id);
                toast.success("Signature annulée");
            } else {
                const note = window.prompt(
                    `Valider DÉFINITIVEMENT le devis de "${d.contractor_name}" ?\n\n` +
                    `Il sera archivé dans l'étude de marché AB Promotion comme référence historique.\n\n` +
                    `Commentaire (facultatif) — ex. raison du choix, conditions négociées :`,
                    ""
                );
                if (note === null) return;
                await api.signDevis(d.id, note || null);
                toast.success(
                    `${d.contractor_name} validé définitivement — ajouté à l'étude de marché`
                );
            }
            load();
        } catch (e) {
            console.error("toggleSign failed:", e);
            toast.error("Erreur");
        }
    };


    if (loading) {
        return (
            <div className="p-10 text-sm text-ink-soft">Chargement…</div>
        );
    }
    if (!project) {
        return <div className="p-10">Chantier introuvable</div>;
    }

    const totalHt = devis.reduce((acc, d) => acc + (d.total_ht || 0), 0);
    const signedCount = devis.filter((d) => d.is_signed).length;
    const displayedDevis = showOnlySigned ? devis.filter((d) => d.is_signed) : devis;

    return (
        <div className="min-h-screen">
            {/* Header */}
            <header className="border-b border-border bg-white">
                <div className="max-w-[1400px] mx-auto px-10 py-8">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] text-ink-soft hover:text-ink mb-4"
                        data-testid="back-projects"
                    >
                        <ArrowLeft className="w-3 h-3" />
                        Tous les chantiers
                    </Link>
                    <div className="flex items-end justify-between gap-6">
                        <div>
                            <div className="stat-label mb-2">
                                Chantier · {fmtDate(project.created_at)}
                            </div>
                            <h1 className="font-heading font-extrabold text-4xl lg:text-5xl leading-[0.95] tracking-tight">
                                {project.name}
                            </h1>
                            {project.address && (
                                <div className="mt-2 text-ink-soft flex items-center gap-1.5 text-sm">
                                    <Building2
                                        className="w-3.5 h-3.5"
                                        strokeWidth={1.6}
                                    />
                                    {project.address}
                                </div>
                            )}
                        </div>
                        <div className="flex gap-2 shrink-0">
                            <PublicLinkDialog projectId={id} />
                            <Button
                                variant="outline"
                                className="rounded-none border-ink"
                                onClick={() =>
                                    window.open(api.cctpUrl(id), "_blank")
                                }
                                data-testid="download-cctp-btn"
                            >
                                <Download
                                    className="w-4 h-4 mr-2"
                                    strokeWidth={1.6}
                                />
                                Trame CCTP
                            </Button>
                            <Link
                                to={`/chantiers/${id}/comparaison`}
                                data-testid="compare-link"
                            >
                                <Button className="rounded-none bg-ink text-white hover:bg-ink/90">
                                    <GitCompare
                                        className="w-4 h-4 mr-2"
                                        strokeWidth={1.6}
                                    />
                                    Comparer les devis
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-[1400px] mx-auto px-10 py-8 space-y-8">
                {/* Stats row */}
                <div className="grid grid-cols-4 gap-4">
                    <StatBox label="Devis collectés" value={devis.length} />
                    <StatBox label="Total HT cumulé" value={fmtEUR(totalHt)} mono />
                    <StatBox
                        label="Doublons détectés"
                        value={duplicates.length}
                        warn={duplicates.length > 0}
                    />
                    <StatBox
                        label="Budget estimé"
                        value={
                            project.budget_estimate
                                ? fmtEUR(project.budget_estimate)
                                : "—"
                        }
                        mono
                    />
                </div>

                {/* Budget table */}
                <BudgetTable
                    projectId={id}
                    refreshKey={`${devis.length}-${devis.map((d) => d.id + d.status).join(",")}`}
                />

                {/* Duplicates */}
                {duplicates.length > 0 && (
                    <section
                        data-testid="duplicates-section"
                        className="panel border-warn"
                    >
                        <div className="panel-header bg-dup-bg/40 border-warn">
                            <div className="flex items-center gap-2.5">
                                <AlertTriangle
                                    className="w-5 h-5 text-warn"
                                    strokeWidth={2}
                                />
                                <h3 className="font-heading text-lg font-bold">
                                    {duplicates.length} chevauchement
                                    {duplicates.length > 1 ? "s" : ""} détecté
                                    {duplicates.length > 1 ? "s" : ""}
                                </h3>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={detect}
                                disabled={detecting}
                                className="rounded-none border-ink"
                            >
                                {detecting ? (
                                    <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                                ) : (
                                    <Sparkles
                                        className="w-3.5 h-3.5 mr-2"
                                        strokeWidth={1.6}
                                    />
                                )}
                                Ré-analyser
                            </Button>
                        </div>
                        <div className="divide-y divide-border">
                            {duplicates.map((d) => (
                                <div
                                    key={d.id}
                                    className="px-6 py-4 flex items-start gap-3 dup-row"
                                    data-testid={`duplicate-${d.id}`}
                                >
                                    <Badge
                                        className={`rounded-none uppercase text-[10px] tracking-wider ${
                                            d.severity === "high"
                                                ? "bg-danger text-white border-danger"
                                                : d.severity === "medium"
                                                ? "bg-warn text-white border-warn"
                                                : "bg-ink-soft text-white border-ink-soft"
                                        }`}
                                    >
                                        {d.severity}
                                    </Badge>
                                    <div className="flex-1 grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <div className="text-xs text-ink-soft uppercase tracking-wide">
                                                {d.devis_a_contractor}
                                            </div>
                                            <div className="font-medium">
                                                {d.item_a_desc}
                                            </div>
                                            <div className="num text-xs text-ink-soft">
                                                {fmtEUR(d.item_a_total)}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-ink-soft uppercase tracking-wide">
                                                {d.devis_b_contractor}
                                            </div>
                                            <div className="font-medium">
                                                {d.item_b_desc}
                                            </div>
                                            <div className="num text-xs text-ink-soft">
                                                {fmtEUR(d.item_b_total)}
                                            </div>
                                        </div>
                                        <div className="col-span-2 text-xs text-ink-soft border-t border-warn/40 pt-2 mt-1">
                                            {d.reason}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Omissions / Postes oubliés */}
                <OmissionsSection
                    projectId={id}
                    devisCount={devis.length}
                    refreshKey={`${devis.length}`}
                />

                {/* Devis list */}
                <section className="panel" data-testid="devis-section">
                    <div className="panel-header">
                        <div>
                            <div className="stat-label">
                                Devis du chantier
                            </div>
                            <h2 className="font-heading text-xl font-bold mt-0.5">
                                {devis.length} devis
                                {signedCount > 0 && (
                                    <span className="ml-2 text-base text-accent font-semibold">
                                        · {signedCount} signé{signedCount > 1 ? "s" : ""}
                                    </span>
                                )}
                            </h2>
                            {signedCount > 0 && (
                                <div className="mt-1.5 flex items-center gap-2 text-xs">
                                    <button
                                        onClick={() => setShowOnlySigned((v) => !v)}
                                        className={`pill border ${
                                            showOnlySigned
                                                ? "border-accent text-white bg-accent font-semibold"
                                                : "border-ink/30 text-ink-soft hover:border-accent hover:text-accent"
                                        }`}
                                        data-testid="filter-signed-only"
                                    >
                                        <Award
                                            className="w-3 h-3"
                                            strokeWidth={2.2}
                                            fill={showOnlySigned ? "currentColor" : "none"}
                                        />
                                        {showOnlySigned
                                            ? "Tous les devis"
                                            : "Voir uniquement signés"}
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                onClick={detect}
                                disabled={detecting || devis.length < 2}
                                className="rounded-none border-ink"
                                data-testid="detect-duplicates-btn"
                            >
                                {detecting ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <AlertTriangle
                                        className="w-4 h-4 mr-2"
                                        strokeWidth={1.6}
                                    />
                                )}
                                Détecter les doublons
                            </Button>
                            <AddDevisManualDialog
                                projectId={id}
                                onCreated={load}
                            />
                            <BulkUploadDialog
                                projectId={id}
                                onUploaded={load}
                            />
                            <UploadDevisDialog
                                projectId={id}
                                onUploaded={load}
                            />
                        </div>
                    </div>
                    {devis.length === 0 ? (
                        <div className="p-12 text-center">
                            <FileText
                                className="w-10 h-10 mx-auto mb-3 text-ink-soft"
                                strokeWidth={1.5}
                            />
                            <div className="font-heading text-lg font-bold mb-1">
                                Aucun devis pour le moment
                            </div>
                            <p className="text-sm text-ink-soft mb-5">
                                Importez un PDF, saisissez manuellement ou
                                envoyez un lien public à vos artisans.
                            </p>
                            <div className="flex justify-center gap-2">
                                <UploadDevisDialog
                                    projectId={id}
                                    onUploaded={load}
                                />
                                <BulkUploadDialog
                                    projectId={id}
                                    onUploaded={load}
                                />
                                <AddDevisManualDialog
                                    projectId={id}
                                    onCreated={load}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="divide-y divide-border">
                            {displayedDevis.map((d) => {
                                const pill =
                                    STATUS_PILL[d.status] || STATUS_PILL.recu;
                                const suspectPdf =
                                    d.source === "pdf" &&
                                    d.reliability &&
                                    (d.reliability.level === "red" ||
                                        d.reliability.level === "amber");
                                return (
                                    <div key={d.id}>
                                    <div
                                        data-testid={`devis-row-${d.id}`}
                                        className="px-6 py-5 grid grid-cols-12 gap-4 items-center hover:bg-[#FAFAFA]"
                                    >
                                        <div className="col-span-4">
                                            <div className="font-heading font-bold text-base leading-tight flex items-center gap-2 flex-wrap">
                                                <span>{d.contractor_name}</span>
                                                <ReliabilityBadge
                                                    reliability={d.reliability}
                                                    devisId={d.id}
                                                />
                                                {(d.version || 1) > 1 && (
                                                    <span
                                                        className="pill border-accent text-accent bg-accent/10 font-bold"
                                                        title={`Version ${d.version} — voir l'historique`}
                                                        data-testid={`version-pill-${d.id}`}
                                                    >
                                                        V{d.version}
                                                    </span>
                                                )}
                                                {d.is_signed && (
                                                    <span
                                                        className="pill border-accent text-white bg-accent font-bold uppercase tracking-wider"
                                                        title={
                                                            d.sign_note
                                                                ? `Signé définitivement — ${d.sign_note}`
                                                                : "Signé définitivement (étude de marché)"
                                                        }
                                                        data-testid={`signed-pill-${d.id}`}
                                                    >
                                                        <Award
                                                            className="w-3 h-3"
                                                            strokeWidth={2.5}
                                                            fill="currentColor"
                                                        />
                                                        Signé
                                                    </span>
                                                )}
                                                {d.source === "pdf" && (
                                                    <span
                                                        className="pill border-accent/60 text-accent bg-accent/5"
                                                        title="PDF original disponible"
                                                    >
                                                        <FileText
                                                            className="w-3 h-3"
                                                            strokeWidth={2}
                                                        />
                                                        PDF
                                                    </span>
                                                )}
                                                {d.source === "public_link" && (
                                                    <span
                                                        className="pill border-ok/60 text-ok bg-ok/5"
                                                        title="Reçu via lien public"
                                                    >
                                                        En ligne
                                                    </span>
                                                )}
                                                {d.user_notes && (
                                                    <span
                                                        className="pill border-warn text-warn bg-warn/10"
                                                        title={d.user_notes}
                                                        data-testid={`notes-pill-${d.id}`}
                                                    >
                                                        <StickyNote
                                                            className="w-3 h-3"
                                                            strokeWidth={2}
                                                        />
                                                        Note
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-ink-soft mt-1 flex items-center gap-2">
                                                <span className="pill border-ink/40 text-ink bg-white">
                                                    {tradeLabel(d.trade)}
                                                </span>
                                                {d.reference && (
                                                    <span>
                                                        n° {d.reference}
                                                    </span>
                                                )}
                                                <span>{fmtDate(d.created_at)}</span>
                                            </div>
                                        </div>
                                        <div className="col-span-2 num">
                                            <div className="stat-label">
                                                Postes
                                            </div>
                                            <div className="text-lg font-bold">
                                                {d.line_items?.length || 0}
                                            </div>
                                        </div>
                                        <div className="col-span-2 num">
                                            <div className="stat-label">
                                                Total HT
                                            </div>
                                            <div className="text-base font-semibold">
                                                {fmtEUR(d.total_ht)}
                                            </div>
                                        </div>
                                        <div className="col-span-1">
                                            <span
                                                className={`pill ${pill.c}`}
                                            >
                                                {pill.l}
                                            </span>
                                        </div>
                                        <div className="col-span-3 flex items-center justify-end gap-1.5 flex-wrap">
                                            <button
                                                onClick={() => toggleSign(d)}
                                                title={
                                                    d.is_signed
                                                        ? "Annuler la signature (devis sortira de l'étude de marché)"
                                                        : "Valider définitivement ce devis (contrat signé) — l'enverra dans l'étude de marché"
                                                }
                                                data-testid={`sign-devis-${d.id}`}
                                                className={`p-2 transition-colors ${
                                                    d.is_signed
                                                        ? "text-accent hover:text-ink-soft"
                                                        : "text-ink-soft hover:text-accent"
                                                }`}
                                            >
                                                <Award
                                                    className="w-4 h-4"
                                                    strokeWidth={d.is_signed ? 2.2 : 1.5}
                                                    fill={
                                                        d.is_signed
                                                            ? "currentColor"
                                                            : "none"
                                                    }
                                                />
                                            </button>
                                            <button
                                                onClick={() => toggleValidate(d)}
                                                title={
                                                    d.status === "valide"
                                                        ? "Retirer la validation budgétaire"
                                                        : "Valider ce devis dans le budget (le retiendra comme meilleure offre du poste)"
                                                }
                                                data-testid={`validate-devis-${d.id}`}
                                                className={`p-2 transition-colors ${
                                                    d.status === "valide"
                                                        ? "text-ok hover:text-ink-soft"
                                                        : "text-ink-soft hover:text-ok"
                                                }`}
                                            >
                                                {d.status === "valide" ? (
                                                    <RotateCcw
                                                        className="w-4 h-4"
                                                        strokeWidth={1.6}
                                                    />
                                                ) : (
                                                    <CheckCircle2
                                                        className="w-4 h-4"
                                                        strokeWidth={1.6}
                                                    />
                                                )}
                                            </button>
                                            <DevisDetailsDialog
                                                devis={d}
                                                onUpdated={load}
                                            />
                                            <EditDevisHeaderDialog
                                                devis={d}
                                                onSaved={load}
                                            />
                                            <RevisionsDialog
                                                devis={d}
                                                onChanged={load}
                                            />
                                            <NegotiationDialog devis={d} />
                                            <MessagesDialog devis={d} />
                                            {d.source === "pdf" && (
                                                <button
                                                    onClick={() => forceOCR(d)}
                                                    disabled={ocringId === d.id}
                                                    className={`p-2 text-ink-soft hover:text-accent ${
                                                        ocringId === d.id
                                                            ? "animate-pulse text-accent"
                                                            : ""
                                                    }`}
                                                    title="Re-extraire le devis via OCR Gemini Vision (utile si l'IA a mal lu le PDF, notamment les scans)"
                                                    data-testid={`force-ocr-${d.id}`}
                                                >
                                                    <ScanText
                                                        className="w-4 h-4"
                                                        strokeWidth={1.5}
                                                    />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => deleteDevis(d.id)}
                                                className="p-2 text-ink-soft hover:text-danger"
                                                data-testid={`delete-devis-${d.id}`}
                                            >
                                                <Trash2
                                                    className="w-4 h-4"
                                                    strokeWidth={1.5}
                                                />
                                            </button>
                                        </div>
                                    </div>
                                    {suspectPdf && (
                                        <div
                                            className="px-6 py-2.5 bg-warn/5 border-t border-warn/30 flex items-center justify-between gap-3 text-sm"
                                            data-testid={`reocr-suggest-${d.id}`}
                                        >
                                            <div className="flex items-center gap-2 min-w-0">
                                                <ScanText
                                                    className="w-4 h-4 text-warn shrink-0"
                                                    strokeWidth={2}
                                                />
                                                <span className="text-ink-soft truncate">
                                                    <b>Extraction incomplète détectée</b> —{" "}
                                                    {(d.reliability?.issues || []).slice(0, 1).join("")}.
                                                    Voulez-vous re-extraire ce PDF via OCR Gemini Vision ?
                                                </span>
                                            </div>
                                            <button
                                                onClick={() => forceOCR(d)}
                                                disabled={ocringId === d.id}
                                                className="shrink-0 bg-accent text-white px-3 py-1.5 text-xs font-semibold uppercase tracking-wide hover:bg-accent-hover disabled:opacity-50"
                                                data-testid={`reocr-suggest-btn-${d.id}`}
                                            >
                                                {ocringId === d.id
                                                    ? "OCR en cours…"
                                                    : "Lancer le re-OCR"}
                                            </button>
                                        </div>
                                    )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>
            </main>

            {/* Floating AI assistant */}
            <ChatAssistant projectId={id} />
        </div>
    );
}

function StatBox({ label, value, warn, mono }) {
    return (
        <div className="panel p-5">
            <div className="stat-label">{label}</div>
            <div
                className={`mt-2 text-3xl font-heading font-extrabold ${
                    warn ? "text-warn" : ""
                } ${mono ? "num" : ""}`}
            >
                {value}
            </div>
        </div>
    );
}
